import { useConfirmation } from "src/sudokit/components/ConfirmationDialog/ConfirmationProvider";
import { DndContext, DragOverlay, MeasuringStrategy } from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import * as Sentry from "@sentry/react";
import classNames from "classnames";
import { motion } from "framer-motion";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { default as InlineSVG, default as SVG } from "react-inlinesvg";
import ButtonSelect from "src/sudokit/components/SudoSelect/ButtonSelect.js";
import ThreeDotsSelect from "src/sudokit/components/SudoSelect/ThreeDotsSelect.js";
import { extractTextFromTipTap } from "../../../../src/shared/schemas/CharacterSchema";
import { CustomFieldSchema, MAX_NUM_CHARACTERS_PER_PROJECT } from "../../../../src/shared/schemas/CharacterSchema.js";
import { formatCharacterRoleLabel, normalizeCharacterRole } from "../../../../src/shared/characterTemplates.js";
import { AppEventPublisher, StoryBibleAction, StoryBibleGenerateType, StoryBibleType } from "../../AppEventPublisher.js";
import Auth from "../../Auth.js";
import { invalidateStoryBibleSummaryCaches, storyBibleCacheProjectIdsForEntities } from "../../lib/storyBibleSummaryCache.js";
import useClickedOutside from "../../hooks/useClickedOutside.js";
import { useMultiSelect } from "../../hooks/useMultiSelect.js";
import { useRefetchOnFocus } from "../../hooks/useRefetchOnFocus";
import { useStableCallback } from "../../hooks/useStableCallback";
import { useProjectStore } from "../../stores/projectStore.js";
import { useStoryBibleStore } from "../../stores/storyBibleStore.js";
import { useCrossAppSignalStore, CrossAppSignal } from "../../stores/crossAppSignalStore";
import { useStickyTourStore } from "../../stores/stickyTourStore";
import { TourSignal } from "../StickyTour/TourTypes";
import Character from "../Character/index.js";
import CharacterUpgradeOffer from "../CharacterUpgradeOffer/index.js";
import CustomizeEntityDefaults from "../CustomizeEntityDefaults/index.js";
import ImportCharacters from "../Modals/ImportCharacters/index.js";
import HoverTippable from "../StoryBible/HoverTippable/index.js";
import { COMPRESS_BUTTON_TITLES_BREAKPOINT } from "../StoryBible/index.js";
import SeriesTag from "../StoryBible/SeriesTag.tsx";
import { characterToPlainText } from "../StoryBible/Utils";
import { useToast } from "../ToastWrapper/index.js";
import { mergeCustomFieldsWithTemplateOrder } from "src/utils/mergeCustomFieldsWithTemplateOrder.js";
import AddCharacterGuidance from "./AddCharacterGuidance";
import GenerateCharacterForm from "./GenerateCharacterForm.js";
import { Container } from "./styles.js";
import { useCharacterTemplates } from "./useCharacterTemplates.js";
import { DroppableArea, SortableCharacter, useReorderCharacters } from "./useReorderCharacters.js";
import StickyNoteAnchor from "../StickyTour/StickyNoteAnchor";
import { AnchorIds } from "../StickyTour/TourTypes";
import { useSudowriteStore } from "../../stores/sudowriteStore.js";
import { useSudoLayoutEventEmitter } from "src/lib/layout-events";
import { usePersistentChatStore } from "src/stores/persistentChatStore";
import { isGenerateCastCtaBlocking } from "src/components/PersistentChat/storyBibleCta";
import { useGenerateCastCtaDisplayProgress } from "src/components/PersistentChat/useStoryBibleCtaDisplayProgress";
import { primaryCtaFillGradient } from "src/components/PersistentChat/storyBibleCtaProgress";
import { StoryBibleEntityLoadStatus } from "../StoryBible/StoryBibleEntityLoadStatus";
import { mergeSavedUpdatedAts } from "../StoryBible/entitySync";

const COMPRESS_SERIES_BUTTON_BREAKPOINT = 600;

/**
 * Converts all characters to plain text, separated by double newlines
 */
function charactersToPlainText(characters) {
  if (!characters || characters.length === 0) return "";

  return characters
    .map(characterToPlainText)
    .filter((text) => text) // Filter out empty character representations
    .join("\n\n");
}

export default function Characters({ projectId, collapsed = false, onToggleCollapse, onSetCollapsed }) {
  const confirmAction = useConfirmation(projectId);
  const [characters, setCharactersState] = useState([]);
  const [initialLoaded, setInitialLoaded] = useState(false);
  const [creating, setCreating] = useState(false);
  const [isCustomizeCharacters, setIsCustomizeCharacters] = useState(false);
  const [isCustomizingWhileImporting, setIsCustomizingWhileImporting] = useState(false);
  const [_generatingCharacter, setGeneratingCharacter] = useState(false);
  const isAgentGeneratingCharacter = useCrossAppSignalStore((state) => state.signals[CrossAppSignal.AGENTIC_CHARACTER_RUNNING]);
  const generatingCharacter = _generatingCharacter || isAgentGeneratingCharacter;
  const hasAccessToAgenticChat = useSudowriteStore((state) => state.subscriptionJSON?.hasAccessToAgenticChat);
  const prevAgentGenerating = useRef(false);
  useEffect(() => {
    if (isAgentGeneratingCharacter && !prevAgentGenerating.current) {
      onSetCollapsed(false);
      setTimeout(() => {
        generateCharacterFormRef?.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 200);
    }
    prevAgentGenerating.current = !!isAgentGeneratingCharacter;
  }, [isAgentGeneratingCharacter, onSetCollapsed]);
  const emitLayoutEvent = useSudoLayoutEventEmitter();
  const globalCtaActive = usePersistentChatStore((state) => {
    const cta = state.ctaAction;
    if (!cta) return false;
    if (isGenerateCastCtaBlocking(cta)) return true;
    return cta.status === "in_progress";
  });
  const isAgentGeneratingCast = useCrossAppSignalStore((state) => state.signals[CrossAppSignal.AGENTIC_CAST_RUNNING]);
  const { active: agenticCastCtaInProgress, progress: agenticCastProgress } = useGenerateCastCtaDisplayProgress();
  const [_generatingCast, setGeneratingCast] = useState(false);
  const generatingCast = hasAccessToAgenticChat
    ? (_generatingCast || agenticCastCtaInProgress || isAgentGeneratingCast)
    : (_generatingCast || isAgentGeneratingCast);
  const endOfListRef = useRef(null);
  const generateCharacterFormRef = useRef(null);
  const [addingDefaultFields, setAddingDefaultFields] = useState(false);
  const [showGuidance, setShowGuidance] = useState(false);
  const [showGuidanceBottom, setShowGuidanceBottom] = useState(false);
  const [guidance, setGuidance] = useState("");
  const [updatedEntityNames, setUpdatedEntityNames] = useState([]);
  const [doneUpdating, setDoneUpdating] = useState(false);
  const [upgradingCast, setUpgradingCast] = useState(false);
  const [showFullButtonText, setShowFullButtonText] = useState(true);
  const [openImport, setOpenImport] = useState(false);
  const [deletingIds, setDeletingIds] = useState([]);
  const refreshTrashedItems = useProjectStore((state) => state.refreshTrashedItems);
  const { setToast } = useToast();
  const [showGenerateCharacterForm, setShowGenerateCharacterForm] = useState(false);
  const [isHoveredFooter, setIsHoveredFooter] = useState(false);
  const [syncing, setSyncing] = useState(false); // This is meant for external syncs that happen when you focus into the editor

  const seed = useStoryBibleStore((state) => state.seed);
  const synopsis = useStoryBibleStore((state) => state.synopsis);
  const project = useProjectStore((state) => state.project);
  const setRequesting = useStoryBibleStore((state) => state.setRequesting); // we use this to disable other SB buttons while generating
  const columnWidths = useProjectStore((state) => state.columnWidths);
  const refreshCharacters = useStoryBibleStore((state) => state.refreshCharacters);
  const syncCharactersEntitiesFromList = useStoryBibleStore((state) => state.syncCharactersEntitiesFromList);
  const upsertCharacterEntity = useStoryBibleStore((state) => state.upsertCharacterEntity);
  const charactersEntities = useStoryBibleStore((state) => state.charactersEntities);
  const charactersEntitiesProjectId = useStoryBibleStore((state) => state.charactersEntitiesProjectId);
  const charactersLoading = useStoryBibleStore((state) => !!state.refreshingCharactersByProjectId[projectId]);
  const charactersLoadFailed = useStoryBibleStore((state) => !!state.charactersLoadFailuresByProjectId[projectId]);
  const characterRefreshTrigger = useStoryBibleStore((state) => state.characterRefreshTrigger);
  const lastSyncedCharacterRefreshTriggerRef = useRef(null);
  const sendFlowSignal = useStickyTourStore((state) => state.sendFlowSignal);
  const sendFailureSignal = useStickyTourStore((state) => state.sendFailureSignal);
  const [compressSeriesButton, setCompressSeriesButton] = useState(false);
  const forceRenderSearchables = useStoryBibleStore((state) => state.forceRenderSearchables);
  const expandedForSearchRef = useRef(new Set());
  const prevForceRenderSearchablesRef = useRef(forceRenderSearchables);
  const charactersRef = useRef(characters);

  // Keep ref in sync with latest value
  useEffect(() => {
    charactersRef.current = characters;
  }, [characters]);

  const allCollapsed = useMemo(() => {
    return characters.length > 0 && characters.every((c) => c.collapsed);
  }, [characters]);

  const { templates: characterTemplates, roleOptions: characterRoleOptions, refreshTemplates: refreshCharacterTemplates, getTemplateForRole } = useCharacterTemplates();
  const [selectedRole, setSelectedRole] = useState("protagonist");
  useEffect(() => {
    if (!characterRoleOptions.length) return;
    if (!characterRoleOptions.some((option) => option.value === selectedRole)) {
      setSelectedRole(characterRoleOptions[0].value);
    }
  }, [characterRoleOptions, selectedRole]);

  // list of elements that should not trigger selection/deselection when clicked
  const interactiveSelectors = [
    "p",
    "svg",
    ".hotdog-dots",
    "input",
    "div.key",
    ".role-selector",
    ".field-container",
    ".field-hints-container",
    ".field-hint-container",
    ".text-button-wrapper",
    ".actions",
    "editor-options-container",
    ".guidance-container",
  ];
  const {
    selectedItems, //
    handleSelect,
    isSelected,
    clearSelection,
  } = useMultiSelect(characters, interactiveSelectors, "characters");

  const hideGuidance = useCallback(() => {
    if (showGuidance || showGuidanceBottom) {
      setShowGuidance(false);
      setShowGuidanceBottom(false);
    }
  }, [showGuidance, showGuidanceBottom]);

  const handleShowGenerateCharacterForm = useCallback((position) => {
    AppEventPublisher.storyBible.characters.openGenerate(position); // change this when we add bottom
    setShowGenerateCharacterForm(true);
    generateCharacterFormRef?.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    setTimeout(() => {
      generateCharacterFormRef?.current?.querySelector("textarea")?.focus({ preventScroll: true });
    }, 300);
  }, []);

  const guidanceRef = useClickedOutside(hideGuidance);

  const setCharacters = useCallback((characters, syncStore = true) => {
    if (useProjectStore.getState().project?.id !== projectId) return;
    if (typeof characters === "function") {
      const store = useStoryBibleStore.getState();
      characters = characters(store.charactersEntitiesProjectId === projectId ? store.charactersEntities : charactersRef.current);
    }
    if (!Array.isArray(characters)) {
      Sentry.captureMessage("Characters.setCharacters received non-array input", {
        level: "warning",
        extra: { charactersType: typeof characters, projectId },
      });
      characters = [];
    }
    const withFloats = characters.filter(Boolean).map((c) => ({ ...c, displayOrder: parseFloat(c.displayOrder || 0) }));
    // Deduplicate by id, keeping the last occurrence (most up-to-date)
    const deduped = [...new Map(withFloats.map((c) => [c.id, c])).values()];
    const sorted = deduped.sort((a, b) => a.displayOrder - b.displayOrder);
    charactersRef.current = sorted;
    setCharactersState(sorted);
    // A subsequent single-card upsert republishes this list to the sidebar.
    // Keep local order/collapse edits in that snapshot, without a fetch.
    if (syncStore) syncCharactersEntitiesFromList(sorted, projectId);
  }, [projectId, syncCharactersEntitiesFromList]);

  const loadCharacters = useCallback(
    async (external) => {
      try {
        if (external) {
          setSyncing(true);
        }
        await refreshCharacters(projectId, { force: external, shouldApply: () => useProjectStore.getState().project?.id === projectId });
      } catch (e) {
        Sentry.captureException(e, { tags: { projectId, charactersBeta: true } });
        setToast("Failed to load characters");
      } finally {
        if (external) {
          setSyncing(false);
        }
      }
    },
    [projectId, refreshCharacters, setToast]
  );

  useRefetchOnFocus(loadCharacters, [projectId, loadCharacters], { skipInitialFetch: true });

  const loadCharactersRef = useRef(loadCharacters);

  useEffect(() => {
    loadCharactersRef.current = loadCharacters;
  }, [loadCharacters]);

  const deleteCharacters = useCallback(
    async (charactersToDelete) => {
      if (charactersToDelete.length === 0) return;
      AppEventPublisher.storyBible.action(StoryBibleType.characters, StoryBibleAction.Delete);
      // Hides the characters from the UI
      setDeletingIds((prev) => [...prev, ...charactersToDelete.map((c) => c.id)]);

      try {
        // Track names for toast message
        const charNames = charactersToDelete.map((c) => extractTextFromTipTap(c.name));
        const deletedIds = [];

        for (const char of charactersToDelete) {
          await Auth.fetchWithIdToken(`/api/characters/${char.id}`, {
            method: "DELETE",
            character: char,
            projectId,
          });
          deletedIds.push(char.id);
        }

        const restoreAll = async () => {
          for (const id of deletedIds) {
            await Auth.fetchWithIdToken(`/api/characters/${id}/restore`, {
              method: "POST",
            });
          }

          const message =
            deletedIds.length === 1 ? `Character "${charNames[0]}" restored` : `${deletedIds.length} characters restored`;

          setToast(message);
          refreshTrashedItems();
          await loadCharactersRef.current();
        };

        const message =
          deletedIds.length === 1 ? (
            <>
              Character "{charNames[0]}" deleted. <a onClick={restoreAll}>Undo</a>
            </>
          ) : (
            <>
              {deletedIds.length} characters deleted. <a onClick={restoreAll}>Undo</a>
            </>
          );

        setToast(message);

        // Refresh data
        refreshTrashedItems();
        await loadCharactersRef.current();

        clearSelection();
      } catch (error) {
        setToast("Something went wrong deleting characters. Please try again.");
      } finally {
        setDeletingIds((prev) => prev.filter((id) => !charactersToDelete.map((c) => c.id).includes(id)));
      }
    },
    [projectId, setToast, refreshTrashedItems, clearSelection],
  );

  useEffect(() => {
    setInitialLoaded(false);
    lastSyncedCharacterRefreshTriggerRef.current = null;
    setCharactersState([]);
  }, [projectId]);

  useEffect(() => {
    if (charactersEntitiesProjectId !== projectId) return;
    if (characterRefreshTrigger === lastSyncedCharacterRefreshTriggerRef.current) return;
    lastSyncedCharacterRefreshTriggerRef.current = characterRefreshTrigger;
    setCharacters(charactersEntities, false);
    setInitialLoaded(true);
  }, [projectId, characterRefreshTrigger, charactersEntities, charactersEntitiesProjectId, setCharacters]);

  const generateCharacter = async ({ guidance = "", role, createNewCharacter } = {}) => {
    try {
      setGeneratingCharacter(true);
      AppEventPublisher.storyBible.generate(StoryBibleType.characters);
      const displayOrder = Math.max(...characters.map((c) => c.displayOrder), 0) + 1;
      const payload = { projectId, seed, synopsis, displayOrder, guidance };
      if (role) {
        payload.role = role;
      }
      if (createNewCharacter === true) {
        payload.createNewCharacter = true;
      }
      const response = await Auth.fetchWithIdToken("/api/ai/storyEngine/character", payload);
      setGeneratingCharacter(false);
      if (useProjectStore.getState().project?.id !== projectId) return;
      setCharacters(current => [...current, response.character]);
      upsertCharacterEntity(response.character, projectId);
      setShowGenerateCharacterForm(false);

      onSetCollapsed(false); // force uncollapse so scrolling works
      setTimeout(() => {
        endOfListRef.current?.scrollIntoView({ behavior: "smooth" });
      }, 200); // wait for the collapse to complete
      refreshCharacters(projectId, { force: true, shouldApply: () => useProjectStore.getState().project?.id === projectId });
    } catch (err) {
      Sentry.captureException(err, { tags: { projectId, charactersBeta: true } });
      setToast("Failed to generate character");
      setGeneratingCharacter(false);
    }
  };

  const generateCast = async () => {
    if (hasAccessToAgenticChat) {
      const activeCta = usePersistentChatStore.getState().ctaAction;
      if (activeCta?.type !== "generate_cast" && globalCtaActive) return;
      sendFlowSignal(TourSignal.STORY_BIBLE_CAST_GENERATE_CLICKED);
      emitLayoutEvent("trigger_cta_chat", {
        type: "generate_cast",
        displayTitle: "Generate All Characters",
        userMessage: "Generate All Characters",
        cannedResponse: "Ok, let's do it!",
      });
      return;
    }
    try {
      sendFlowSignal(TourSignal.STORY_BIBLE_CAST_GENERATE_CLICKED);
      AppEventPublisher.storyBible.generate(StoryBibleGenerateType.CharacterCast);
      setRequesting(true);
      setGeneratingCast(true);

      const response = await Auth.fetchWithIdToken("/api/ai/storyEngine/characterCast", { projectId, seed, synopsis });
      setGeneratingCast(false);
      setRequesting(false);
      if (useProjectStore.getState().project?.id !== projectId) return;
      const newCast = Array.isArray(response.cast) ? response.cast : [];
      setCharacters(current => [...current, ...newCast]);
      if (newCast.length === 0) {
        if (response.noCharactersFromCastList) {
          setToast("No new characters were needed based on your Story Bible.");
        } else {
          Sentry.captureMessage("Character cast generation returned no valid characters", {
            level: "warning",
            extra: { projectId },
          });
        }
      }
      refreshCharacters(projectId, { force: true, shouldApply: () => useProjectStore.getState().project?.id === projectId });
      sendFlowSignal(TourSignal.STORY_BIBLE_CAST_GENERATE_COMPLETED);
    } catch (err) {
      Sentry.captureException(err, { tags: { projectId, charactersBeta: true } });
      setToast("Failed to generate cast");
      setGeneratingCast(false);
      setRequesting(false);
      sendFailureSignal(TourSignal.STORY_BIBLE_CAST_GENERATE_FAILED);
    }
  };

  const handleUpgradeCast = async () => {
    try {
      setUpgradingCast(true);
      const result = await Auth.fetchWithIdToken("/api/ai/storyEngine/upgradeCast", { projectId });
      if (useProjectStore.getState().project?.id !== projectId) return;
      setCharacters(current => [...current, ...result.cast]);
      refreshCharacters(projectId, { force: true, shouldApply: () => useProjectStore.getState().project?.id === projectId });
      setToast("Upgraded characters");
    } catch (err) {
      Sentry.captureException(err, { tags: { projectId, charactersBeta: true } });
      setToast("Problem enhancing characters");
    } finally {
      setUpgradingCast(false);
    }
  };

  const handleGenerateRequested = (position) => {
    AppEventPublisher.storyBible.characters.openGenerate(position);
    setShowGuidance(true);
  };

  const handleGenerateSubmitted = useCallback(
    (e) => {
      e?.preventDefault();
      hideGuidance();
      generateCharacter({ guidance, role: selectedRole });
      setGuidance("");
    },
    [generateCharacter, guidance, hideGuidance, selectedRole]
  );

  const handleRandomGenerate = useCallback(
    (e) => {
      setShowGenerateCharacterForm(true);
      generateCharacterFormRef?.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      e?.preventDefault();
      setGuidance("");
      hideGuidance();
      generateCharacter({ guidance: "", createNewCharacter: true });
    },
    [generateCharacter, hideGuidance]
  );

  const create = async (position) => {
    try {
      setCreating(true);
      if (characters && characters.length >= MAX_NUM_CHARACTERS_PER_PROJECT) {
        setToast(`You can only have ${MAX_NUM_CHARACTERS_PER_PROJECT} characters per project`);
        return;
      }
      AppEventPublisher.storyBible.characters.addBlankCharacter(position);
      const maxDisplayOrder = characters.reduce((max, character) => Math.max(max, character.displayOrder), 0);
      const result = await Auth.fetchWithIdToken("/api/characters/", {
        displayOrder: maxDisplayOrder + 1,
        projectId,
      });
      setCreating(false);
      if (useProjectStore.getState().project?.id !== projectId) return;
      upsertCharacterEntity(result.character, projectId);

      await loadCharacters();
      endOfListRef.current?.scrollIntoView({ behavior: "smooth" });
    } catch (err) {
      Sentry.captureException(err, { tags: { projectId, charactersBeta: true } });
      setToast("Failed to create character");
      setCreating(false);
    }
  };

  const updateCharacterData = useCallback((character) => {
    setCharacters(current => current.map(c => c.id === character.id ? character : c));
  }, [setCharacters]);

  const { draggingItem, onDragEnd, onDragStart, onDragCancel, draggingLock } = useReorderCharacters({ characters, projectId, updateCharacterData });

  const handleGuidanceKeys = (e) => {
    if (e.key === "Escape") {
      hideGuidance();
    }
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
      handleGenerateSubmitted(e);
    }
  };

  const normalizeRoleValue = (value) => {
    if (!value) return "";
    return normalizeCharacterRole(value);
  };

  const getCharacterRoleSlugs = (rolesValue) => {
    if (!rolesValue || typeof rolesValue !== "string") return [];
    return rolesValue
      .split(",")
      .map((role) => role.trim())
      .filter(Boolean)
      .map((role) => normalizeRoleValue(role));
  };

  const addDefaultsToCurrentCharacters = async (fields, entityType) => {
    if (characters.length <= 0) return;

    const normalizedRole = normalizeRoleValue(entityType);
    if (!normalizedRole) return;

    const charactersToUpdate = characters.filter((character) => {
      const roleSlugs = getCharacterRoleSlugs(character.roles);
      return roleSlugs.includes(normalizedRole);
    });

    if (charactersToUpdate.length === 0) {
      setToast(`No ${formatCharacterRoleLabel(normalizedRole)}s to update`);
      return;
    }

    setAddingDefaultFields(true);

    try {
      const results = await Promise.allSettled(
        charactersToUpdate.map(async (character) => {
          const currentFields = character.customFields;
          const updatedFields = mergeCustomFieldsWithTemplateOrder(
            currentFields,
            fields,
            (tf) => CustomFieldSchema.cast({ label: tf.label || "", id: tf.id }),
          );
          const updatedCharacter = { ...character, customFields: updatedFields };

          const result = await Auth.fetchWithIdToken(`/api/characters/${character.id}`, {
            method: "PUT",
            character: updatedCharacter,
            projectId,
            deferSummaryCacheInvalidation: true,
          });
          setUpdatedEntityNames((prev) => [...prev, extractTextFromTipTap(result.character.name)]);
        })
      );

      if (results.some((result) => result.status === "fulfilled")) {
        await invalidateStoryBibleSummaryCaches(
          storyBibleCacheProjectIdsForEntities(charactersToUpdate, projectId),
          ["characters"],
        );
      }

      if (results.some((result) => result.status === "rejected")) {
        throw results.find((result) => result.status === "rejected").reason;
      }

      await loadCharacters();
      setDoneUpdating(true);
    } catch (error) {
      setToast("Failed to add traits to selected characters");
      console.error("Failed to add traits to characters", error);
    } finally {
      setAddingDefaultFields(false);
    }
  };
  const draggedCharacter = characters.find((character) => character.id === draggingItem);
  const showCastGeneration = initialLoaded && characters.length === 0 && (synopsis || seed);
  const showCastGenerationBottom = characters.length > 0;

  const toggleCustomizingCharacters = (value) => {
    setIsCustomizeCharacters(value);
    setUpdatedEntityNames([]);
    setDoneUpdating(false);
  };

  const hideList = upgradingCast || addingDefaultFields;

  useEffect(() => {
    if (columnWidths.center.current <= COMPRESS_BUTTON_TITLES_BREAKPOINT) {
      setShowFullButtonText(false);
    } else {
      setShowFullButtonText(true);
    }

    if (columnWidths.center.current <= COMPRESS_SERIES_BUTTON_BREAKPOINT) {
      setCompressSeriesButton(true);
    } else {
      setCompressSeriesButton(false);
    }
  }, [columnWidths.center]);

  // Cancel handle for in-progress batched expansion
  const expandCancelRef = useRef(null);

  // Expand collapsed characters when Find/Replace opens, restore when it closes
  // PERF: Expand in batches to avoid blocking the main thread for ~3 seconds
  useEffect(() => {
    const currentCharacters = charactersRef.current;

    // Safety check: ensure characters is an array
    if (!Array.isArray(currentCharacters)) return;

    const prevValue = prevForceRenderSearchablesRef.current;
    prevForceRenderSearchablesRef.current = forceRenderSearchables;

    // Only act on transitions (false -> true or true -> false)
    if (forceRenderSearchables && !prevValue) {
      // Cancel any in-progress expansion from a previous open
      if (expandCancelRef.current) expandCancelRef.current();

      // Find/Replace opened - expand all collapsed characters and track which ones were expanded
      const collapsedIds = currentCharacters.filter((c) => c && c.collapsed).map((c) => c.id);
      expandedForSearchRef.current = new Set(collapsedIds);

      // PERF: Expand in batches of 10 to keep each render under ~150ms.
      // This lets the modal appear instantly and keeps typing responsive.
      if (collapsedIds.length > 0) {
        const BATCH_SIZE = 10;
        let offset = 0;
        let cancelled = false;
        expandCancelRef.current = () => { cancelled = true; };

        const expandNextBatch = () => {
          if (cancelled || offset >= collapsedIds.length) return;

          const batchIds = new Set(collapsedIds.slice(offset, offset + BATCH_SIZE));
          offset += BATCH_SIZE;

          const chars = charactersRef.current;
          if (!Array.isArray(chars)) return;
          const updated = chars.map((c) => c && batchIds.has(c.id) ? { ...c, collapsed: false } : c);
          setCharacters(updated);

          if (offset < collapsedIds.length) {
            setTimeout(expandNextBatch, 0);
          }
        };

        // Start first batch on next frame so the modal can render first
        setTimeout(expandNextBatch, 0);
      }
    } else if (!forceRenderSearchables && prevValue) {
      // Cancel any in-progress expansion
      if (expandCancelRef.current) expandCancelRef.current();
      expandCancelRef.current = null;

      // Find/Replace closed - re-collapse only the characters that were expanded for search
      const idsToCollapse = Array.from(expandedForSearchRef.current);
      if (idsToCollapse.length > 0 && currentCharacters.length > 0) {
        const updatedCharacters = currentCharacters.map((c) => (c && idsToCollapse.includes(c.id) ? { ...c, collapsed: true } : c));
        setCharacters(updatedCharacters);
        expandedForSearchRef.current.clear();
      }
    }

    // Cancel pending batched expansion on unmount
    return () => {
      if (expandCancelRef.current) expandCancelRef.current();
    };
  }, [forceRenderSearchables]);

  const handleCopyCharacters = async () => {
    if (!characters || characters.length === 0) {
      setToast("No characters to copy");
      return;
    }

    try {
      const plainText = charactersToPlainText(characters);
      await navigator.clipboard.writeText(plainText);
      setToast("Characters copied");
      AppEventPublisher.storyBible.characters.copyAllCharacters();
    } catch (error) {
      setToast("Oops, copying failed. Try again");
    }
  };

  const handleExportCharacters = async () => {
    AppEventPublisher.storyBible.action(StoryBibleType.characters, StoryBibleAction.Export);
    project.exportCharacters(projectId);
  };

  const handleImportCharacters = () => {
    AppEventPublisher.storyBible.action(StoryBibleType.characters, StoryBibleAction.Import);
    setOpenImport(true);
  };

  const handleCollapseExpandAll = async () => {
    const shouldCollapse = !allCollapsed;
    const updatedCharacters = characters.map((c) => ({ ...c, collapsed: shouldCollapse }));
    setCharacters(updatedCharacters);

    try {
      const results = await Promise.allSettled(
        updatedCharacters.map((character) =>
          Auth.fetchWithIdToken(`/api/characters/${character.id}`, {
            method: "PUT",
            character: { id: character.id, collapsed: shouldCollapse },
            metadataOnly: true,
            updatedAt: character.updatedAt,
            projectId,
            deferSummaryCacheInvalidation: true,
          })
        )
      );
      const savedCharacters = results
        .filter((result) => result.status === "fulfilled" && result.value?.character)
        .map((result) => result.value.character);
      const currentCharacters = charactersRef.current;
      const mergedCharacters = mergeSavedUpdatedAts(currentCharacters, savedCharacters);
      if (mergedCharacters !== currentCharacters) {
        setCharacters(mergedCharacters);
      }
      if (results.some((result) => result.status === "fulfilled")) {
        await invalidateStoryBibleSummaryCaches(
          storyBibleCacheProjectIdsForEntities(updatedCharacters, projectId),
          ["characters"],
        );
      }
      const failed = results.find((result) => result.status === "rejected");
      if (failed) {
        Sentry.captureException(failed.reason, { tags: { projectId } });
      }
    } catch (err) {
      Sentry.captureException(err, { tags: { projectId } });
    }
  };

  const actions = {
    copy: {
      label: "Copy",
      icon: (
        <InlineSVG
          src="/assets/duplicate.svg"
          aria-hidden="true"
        />
      ),
      onClick: handleCopyCharacters,
    },
    import: {
      label: "Import",
      icon: (
        <InlineSVG
          src="/assets/import.svg"
          className="import"
          aria-hidden="true"
        />
      ),
      onClick: () => {
        setOpenImport(true);
        AppEventPublisher.storyBible.action(StoryBibleType.characters, StoryBibleAction.Import);
      },
    },
    export: {
      label: "Export",
      icon: (
        <InlineSVG
          src="/assets/export.svg"
          aria-hidden="true"
        />
      ),
      onClick: async () => {
        AppEventPublisher.storyBible.action(StoryBibleType.characters, StoryBibleAction.Export);
        project.exportCharacters(projectId);
      },
    },
  };

  const threeDotsSelectActions = useMemo(() => {
    const collapseExpandLabel = allCollapsed ? "Expand All" : "Collapse All";
    const collapseExpandIcon = allCollapsed ? "/assets/expand-all.svg" : "/assets/collapse-all.svg";
    const actions = [
      { value: "Copy Characters", label: "Copy Characters", icon: { src: "/assets/duplicate.svg", label: "Copy Characters", status: "default" } },
      { value: "Customize Traits", label: "Customize Traits", icon: { src: "/assets/gear-v2.svg", label: "Customize Traits", status: "default" } },
      { value: "Import", label: "Import", icon: { src: "/assets/import-icon.svg", label: "Import", status: "default" } },
      { value: "Export", label: "Export", icon: { src: "/assets/export.svg", label: "Export", status: "default" } },
      { value: collapseExpandLabel, label: collapseExpandLabel, icon: { src: collapseExpandIcon, label: collapseExpandLabel, status: "default" } },
    ];
    if (characters.length > 0) {
      actions.push({
        value: "Delete All",
        label: "Delete All",
        icon: { src: "/assets/delete.svg", label: "Delete All", status: "danger" },
      });
    }
    return actions;
  }, [allCollapsed, characters.length]);

  const handleClickCustomizeDefaults = () => {
    AppEventPublisher.storyBible.customizeDefaults(StoryBibleType.characters);
    setIsCustomizeCharacters(true);
  };

  const handleDeleteAllCharacters = async () => {
    if (characters.length === 0) return;
    const countLabel = characters.length === 1 ? "1 character" : `${characters.length} characters`;
    await confirmAction({
      title: "Delete all Characters",
      message: `Are you sure you want to delete all ${countLabel}? You can restore them from Trash.`,
      actionLabel: "Delete all",
      destructive: true
    }, async () => {
      await deleteCharacters(characters);
    });
  };

  const handleThreeDotsSelectAction = useStableCallback((value) => {
    switch (value) {
      case "Copy Characters":
        handleCopyCharacters();
        break;
      case "Customize Traits":
        handleClickCustomizeDefaults();
        break;
      case "Import":
        handleImportCharacters();
        break;
      case "Export":
        handleExportCharacters();
        break;
      case "Collapse All":
      case "Expand All":
        handleCollapseExpandAll();
        break;
      case "Delete All":
        handleDeleteAllCharacters();
        break;
      default:
        break;
    }
  });

  const addCharacterOptions = useMemo(
    () => [
      { value: "blank-character", label: "Blank Character", icon: { src: "/assets/character-icon.svg" } },
      { value: "generate", label: "Generate Character ", icon: { src: "/assets/AI-sparkle.svg" } },
      { value: "random", label: "Surprise Me", icon: { src: "/assets/dice.svg" } },
    ],
    [],
  );

  const addCharacterButtonProps = useMemo(
    () => ({
      variant: "tertiary",
      loadingPercentage: generatingCharacter ? 100 : undefined,
      state: generatingCharacter ? "loading" : "default",
      size: "small",
      leftIcon: creating ? "" : "/assets/add.svg",
    }),
    [generatingCharacter, creating],
  );

  const addCharacterCastButtonProps = useMemo(
    () => ({
      variant: "tertiary",
      loadingPercentage: generatingCharacter ? 100 : undefined,
      state: generatingCharacter ? "loading" : "default",
      size: "small",
      leftIcon: "/assets/add.svg",
      className: "mb-0.5",
    }),
    [generatingCharacter],
  );

  const runAddCharacterOptionChange = useStableCallback((position, value) => {
    switch (value) {
      case "blank-character":
        create(position);
        break;
      case "generate":
        if (hasAccessToAgenticChat) {
          if (globalCtaActive) break;
          emitLayoutEvent("trigger_cta_chat", {
            type: "generate_character",
            displayTitle: "Generate Character",
            userMessage: "Generate Character",
            cannedResponse: "Ok, let's do it!",
          });
        } else {
          handleShowGenerateCharacterForm(position);
        }
        break;
      case "random":
        handleRandomGenerate();
        break;
      default:
        break;
    }
  });

  const handleAddCharacterOptionChangeTop = useStableCallback((value) => {
    if (collapsed) {
      onToggleCollapse?.();
    }
    setTimeout(() => {
      runAddCharacterOptionChange("top", value);
    }, 200);
  });

  const handleAddCharacterOptionChangeBottom = useStableCallback((value) => {
    runAddCharacterOptionChange("bottom", value);
  });

  const showCharacterUpgradeOffer = project.showCharacterUpgradeOffer && characters.length > 0;

  return (
    <Container
      tabIndex={0}
      className={classNames({ "space-with-characters": characters.length > 0 })}
      $showFullButtonText={showFullButtonText}
    >
      <ImportCharacters
        isOpen={openImport}
        setIsOpen={setOpenImport}
        projectId={projectId}
        characters={characters}
        setCharacters={setCharacters}
        endOfListRef={endOfListRef}
        loadCharacters={loadCharacters}
        toggleCustomizingCharacters={toggleCustomizingCharacters}
        setIsCustomizingWhileImporting={setIsCustomizingWhileImporting}
      />
      <CustomizeEntityDefaults
        singleEntityType="character"
        singleEntityTypeLabel="Character"
        isOpen={isCustomizeCharacters}
        setIsOpen={toggleCustomizingCharacters}
        updateCurrentEntitiesWithFields={addDefaultsToCurrentCharacters}
        addingDefaultFields={addingDefaultFields}
        updatedEntityNames={updatedEntityNames}
        doneUpdating={doneUpdating}
        onDefaultsChanged={refreshCharacterTemplates}
        isCustomizingWhileImporting={isCustomizingWhileImporting}
        setIsCustomizingWhileImporting={setIsCustomizingWhileImporting}
        setImportIsOpen={setOpenImport}
      />
      <div className="characters-card">
        <div className="header">
          <div className="left-side">
            <div
              className={classNames("title-lockup", { "cursor-pointer": onToggleCollapse })}
              style={onToggleCollapse ? { userSelect: "none" } : undefined}
              onClick={(e) => {
                if (!onToggleCollapse) return;
                e.preventDefault();
                onToggleCollapse();
              }}
            >
              {onToggleCollapse && (
                <div className="chevron-wrapper">
                  <SVG
                    src="/assets/chevron-down.svg"
                    className={classNames("collapse-chevron", { "rotated-chevron": collapsed })}
                    aria-label={`${collapsed ? "Expand" : "Collapse"} Characters`}
                  />
                </div>
              )}
              <SVG
                src="/assets/character-icon.svg"
                aria-hidden="true"
              />
              <h2 id="heading-characters">Characters&nbsp;</h2>
            </div>
            {showCastGeneration && <p className="FYI">Generate your Characters one at a time or all at once.</p>}
          </div>
          <div className={`right-side ${showCastGeneration ? 'show-cast' : ''}`}>
            {showCastGeneration && (
              <div
                className="generateCastContainer"
                onClick={(e) => e.stopPropagation()}
              >
                {project.seriesId && (
                  <SeriesTag
                    className="mb-[12px] flex flex-nowrap gap-[5px]"
                    collapsed={compressSeriesButton}
                  />
                )}
                <ButtonSelect
                  label={generatingCharacter ? "Generating" : creating ? "Creating" : compressSeriesButton && project.seriesId ? "Add" : "Add Character"}
                  options={addCharacterOptions}
                  disabled={generatingCharacter || creating || generatingCast}
                  buttonProps={addCharacterCastButtonProps}
                  onChange={handleAddCharacterOptionChangeTop}
                />
                <div className="button-container">
                  <StickyNoteAnchor anchorId={AnchorIds.STORY_BIBLE_GENERATE_CAST_BUTTON} />
                  {!generatingCast && !generatingCharacter && (
                    <HoverTippable id={"button-container-generate-cast"}>
                      <button
                        id="button-generate-cast"
                        onClick={generateCast}
                        className="primary"
                      >
                        <SVG
                          src="/assets/AI-sparkle.svg"
                          aria-hidden="true"
                        />
                        <p>Generate All Characters</p>
                      </button>
                    </HoverTippable>
                  )}
                  {generatingCast && (
                    <div className="generating-cast-container">
                      {" "}
                      {/*just for dark mode*/}
                      <motion.button
                        className="primary"
                        initial={{
                          background: primaryCtaFillGradient(0),
                        }}
                        animate={{
                          background: hasAccessToAgenticChat && agenticCastCtaInProgress
                            ? primaryCtaFillGradient(agenticCastProgress)
                            : primaryCtaFillGradient(0.9),
                          transition: hasAccessToAgenticChat && agenticCastCtaInProgress
                            ? { duration: 0.2, ease: "linear" }
                            : { duration: 60, ease: "linear" },
                        }}
                      >
                        <SVG
                          src="/assets/AI-sparkle.svg"
                          aria-hidden="true"
                        />
                        <p>Generating All Characters</p>
                      </motion.button>
                    </div>
                  )}
                  {!generatingCharacter && (
                    <div className="svg-container">
                      <InlineSVG
                        id="icon-1"
                        src="/assets/character-icon.svg"
                        aria-hidden="true"
                      />
                      <InlineSVG
                        id="icon-2"
                        src="/assets/character-icon.svg"
                        aria-hidden="true"
                      />
                      <InlineSVG
                        id="icon-3"
                        src="/assets/character-icon.svg"
                        aria-hidden="true"
                      />
                      <InlineSVG
                        id="icon-4"
                        src="/assets/character-icon.svg"
                        aria-hidden="true"
                      />
                      <InlineSVG
                        id="icon-5"
                        src="/assets/character-icon.svg"
                        aria-hidden="true"
                      />
                    </div>
                  )}
                </div>
              </div>
            )}
            {!showCastGeneration && (
              <div
                    className="flex items-center gap-[10px]"
                    onClick={(e) => e.stopPropagation()}
                  >
                {project.seriesId && <SeriesTag collapsed={compressSeriesButton} />}
                <StickyNoteAnchor anchorId={AnchorIds.STORY_BIBLE_ADD_CHARACTER_BUTTON} />
                <ButtonSelect
                  label={generatingCharacter ? "Generating" : creating ? "Creating..." : "Add Character"}
                  options={addCharacterOptions}
                  disabled={generatingCharacter || creating}
                  buttonProps={addCharacterButtonProps}
                  onChange={handleAddCharacterOptionChangeTop}
                />
              </div>
            )}
            <div onClick={(e) => e.stopPropagation()}>
              <ThreeDotsSelect
                optionAlignment="left"
                options={threeDotsSelectActions}
                onChange={handleThreeDotsSelectAction}
              />
            </div>
          </div>
        </div>

        {!collapsed && (
          <>
            {showGuidance && (
              <AddCharacterGuidance
                containerRef={guidanceRef}
                value={guidance}
                onChange={setGuidance}
                onKeyDown={handleGuidanceKeys}
                onRandomGenerateClick={handleRandomGenerate}
                disabled={generatingCharacter || generatingCast}
                onGenerateSubmitted={handleGenerateSubmitted}
                className="section-header-position"
                roleOptions={characterRoleOptions}
                selectedRole={selectedRole}
                onSelectRole={setSelectedRole}
                roleSelectId="generate-character-role-top"
              />
            )}
          </>
        )}
      </div>

      {!collapsed && (
        <>
          <StoryBibleEntityLoadStatus
            entityLabel="characters"
            hasContent={characters.length > 0}
            hasError={charactersLoadFailed}
            isLoading={charactersLoading}
            onRetry={() => void loadCharacters(true)}
          />
          {showCharacterUpgradeOffer && (
            <CharacterUpgradeOffer
              onUpgradeCast={handleUpgradeCast}
              upgrading={upgradingCast}
            />
          )}

          {!hideList && (
            <DndContext
              onDragStart={onDragStart}
              onDragEnd={onDragEnd}
              onDragCancel={onDragCancel}
              modifiers={[restrictToVerticalAxis]}
              layoutMeasuring={{ strategy: MeasuringStrategy.Always }}
            >
              <DroppableArea
                characterId="start"
                start={true}
              />
              {characters.map((character, index) => {
                const isDragging = draggingItem === character.id;
                return (
                  <React.Fragment key={`${projectId}:${character.id}`}>
                    {index === characters.length - 1 && <div ref={endOfListRef} />}
                    <SortableCharacter
                      draggingLock={draggingLock}
                      key={character.id}
                      character={character}
                      projectId={projectId}
                      isDragging={isDragging}
                      updateCharacterData={updateCharacterData}
                      isSelected={isSelected(character.id)}
                      handleSelect={handleSelect}
                      onDeleteMultiple={deleteCharacters}
                      isDeleting={deletingIds.includes(character.id)}
                      selectedItems={selectedItems}
                      syncing={syncing}
                      getTemplateForRole={getTemplateForRole}
                      roleOptions={characterRoleOptions}
                    />
                    <DroppableArea
                      start={false}
                      characterId={character.id}
                    />
                  </React.Fragment>
                );
              })}

              <GenerateCharacterForm
                showGenerateCharacterForm={showGenerateCharacterForm || isAgentGeneratingCharacter}
                guidance={guidance}
                setGuidance={setGuidance}
                handleGenerateSubmitted={handleGenerateSubmitted}
                handleCancel={() => setShowGenerateCharacterForm(false)}
                generatingCharacter={generatingCharacter}
                ref={generateCharacterFormRef}
                onKeyDown={handleGuidanceKeys}
                roleOptions={characterRoleOptions}
                selectedRole={selectedRole}
                onSelectRole={setSelectedRole}
                roleSelectId="generate-character-form-role"
              />
              <DragOverlay>
                {draggingItem ? (
                  <div
                    className="entity"
                    style={{ opacity: 0.9 }}
                  >
                    <Character
                      initialCharacter={draggedCharacter}
                      update={loadCharacters}
                      projectId={projectId}
                      isDragging={true}
                      updateCharacterData={updateCharacterData}
                      getTemplateForRole={getTemplateForRole}
                      roleOptions={characterRoleOptions}
                    />
                  </div>
                ) : null}
              </DragOverlay>
            </DndContext>
          )}
          {showCastGenerationBottom && (
            <div
              className="hoverable-footer"
              onMouseEnter={() => setIsHoveredFooter(true)}
              onMouseLeave={() => setIsHoveredFooter(false)}
            >
              {isHoveredFooter && !showGenerateCharacterForm && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{
                    height: { duration: 0.2, ease: "easeInOut" },
                    opacity: { duration: 0.2, ease: "easeInOut" },
                  }}
                  className="flex gap-2 justify-center"
                  style={{ overflow: "hidden" }}
                >
                  <ButtonSelect
                    label={generatingCharacter ? "Generating" : creating ? "Creating..." : "Add Character"}
                    options={addCharacterOptions}
                    disabled={generatingCharacter || creating}
                    buttonProps={addCharacterButtonProps}
                    onChange={handleAddCharacterOptionChangeBottom}
                  />
                </motion.div>
              )}
            </div>
          )}

          {showGuidanceBottom && (
            <AddCharacterGuidance
              containerRef={guidanceRef}
              value={guidance}
              onChange={setGuidance}
              onKeyDown={handleGuidanceKeys}
              onRandomGenerateClick={handleRandomGenerate}
              disabled={generatingCharacter || generatingCast}
              onGenerateSubmitted={handleGenerateSubmitted}
              roleOptions={characterRoleOptions}
              selectedRole={selectedRole}
              onSelectRole={setSelectedRole}
              roleSelectId="generate-character-role-bottom"
            />
          )}
        </>
      )}
    </Container>
  );
}
